import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ChartBuilder.css';
import { getApiBase, normalizeFetchError } from '../utils/apiBase';

interface ApartmentConfigItem {
  apartment_name: string;
}

interface ParsedOrder {
  dong: string;
  ho: string;
  customer_name: string;
  menu: string;
  delivery_date: string;
}

interface CoreMenuSummary {
  total_count: number;
  korean_1_count: number;
  korean_2_count: number;
  simple_count: number;
  salad_count: number;
  sandwich_count: number;
}

interface DashboardApartmentSummary {
  menu_summary: { [key: string]: number };
}

interface DashboardSummaryResponse {
  apartments: DashboardApartmentSummary[];
}

interface LiveSummaryResponse {
  source?: string;
  summary?: {
    total_count?: number;
    korean_1_count?: number;
    korean_2_count?: number;
    simple_count?: number;
    salad_count?: number;
    sandwich_count?: number;
  };
  adjustment?: {
    applied?: boolean;
    cancellation_count?: number;
    removed_count?: number;
    error?: string;
  };
}

interface ChartPayload {
  apartment_name: string;
  apartment_address?: string;
  entrance_password?: string;
  delivery_date: string;
  menu_summary: { [key: string]: number };
  core_menu_summary?: CoreMenuSummary;
  total_count: number;
  orders_by_dong: { [key: string]: ParsedOrder[] };
  is_personal_delivery?: boolean;
  treat_all_as_existing?: boolean;
}

interface AdjustmentSummary {
  applied?: boolean;
  cancellation_count?: number;
  removed_count?: number;
  error?: string;
}

const toCoreMenuSummary = (menuSummary: { [key: string]: number }): CoreMenuSummary => {
  const core: CoreMenuSummary = {
    total_count: 0,
    korean_1_count: 0,
    korean_2_count: 0,
    simple_count: 0,
    salad_count: 0,
    sandwich_count: 0,
  };

  Object.entries(menuSummary || {}).forEach(([rawMenu, rawCount]) => {
    const count = Number(rawCount || 0);
    const normalized = String(rawMenu || '').replace(/\s+/g, '');
    core.total_count += count;
    if (normalized.includes('한식1인')) core.korean_1_count += count;
    else if (normalized.includes('한식2인')) core.korean_2_count += count;
    else if (normalized.includes('간편식')) core.simple_count += count;
    else if (normalized.includes('샐러드') || normalized.includes('그린식')) core.salad_count += count;
    else if (normalized.includes('샌드위치')) core.sandwich_count += count;
  });

  return core;
};

const getTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const ChartBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [apartments, setApartments] = useState<ApartmentConfigItem[]>([]);
  const [apartmentName, setApartmentName] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(getTomorrow());
  const [inputText, setInputText] = useState('');
  const [includeLineSort, setIncludeLineSort] = useState(true);
  const [applyAdjustments, setApplyAdjustments] = useState(true);
  const [treatAllAsExisting, setTreatAllAsExisting] = useState(false);
  const [autoFetching, setAutoFetching] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payload, setPayload] = useState<ChartPayload | null>(null);
  const [previewOrders, setPreviewOrders] = useState<ParsedOrder[]>([]);
  const [registerAllApartments, setRegisterAllApartments] = useState(true);
  const [showAllSummary, setShowAllSummary] = useState(true);
  const [allSummary, setAllSummary] = useState<CoreMenuSummary | null>(null);
  const [allSummarySource, setAllSummarySource] = useState<'dashboard' | 'live' | null>(null);
  const [allSummaryAdjustmentText, setAllSummaryAdjustmentText] = useState<string>('');
  const [allSummaryLoading, setAllSummaryLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const token = localStorage.getItem('token');

  const apiBase = getApiBase();

  const setStatus = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage(text);
    setMessageType(type);
  };

  const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    const status = response.status;
    let bodyText = '';
    try {
      bodyText = (await response.text()).trim();
    } catch {
      bodyText = '';
    }

    if (!bodyText) {
      return `${fallback} (HTTP ${status})`;
    }

    try {
      const parsed = JSON.parse(bodyText);
      const detail = parsed?.detail || parsed?.message || '';
      if (detail) return `${detail} (HTTP ${status})`;
    } catch {
      // plain text/html 본문
    }

    const snippet = bodyText.replace(/\s+/g, ' ').slice(0, 220);
    return `${fallback} (HTTP ${status}): ${snippet}`;
  };

  const fetchApartments = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${apiBase}/api/admin/apartment-configs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }
      if (!response.ok) {
        throw new Error('아파트 목록 조회 실패');
      }

      const data: ApartmentConfigItem[] = await response.json();
      setApartments(data);
      if (!apartmentName && data.length > 0) {
        setApartmentName(data[0].apartment_name);
      }
    } catch (err) {
      console.error(err);
      setStatus(normalizeFetchError(err, apiBase, '아파트 목록을 불러오지 못했습니다.'), 'error');
    }
  };

  useEffect(() => {
    fetchApartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchAllSummary = async () => {
      if (!showAllSummary) return;
      if (!token) return;

      setAllSummaryLoading(true);
      try {
        const response = await fetch(`${apiBase}/api/admin/dashboard/${deliveryDate}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401 || response.status === 403) {
          setAllSummary(null);
          setAllSummarySource(null);
          setAllSummaryAdjustmentText('');
          return;
        }
        if (response.ok) {
          const data: DashboardSummaryResponse = await response.json();
          if (Array.isArray(data.apartments) && data.apartments.length > 0) {
            const mergedMenuSummary: { [key: string]: number } = {};
            (data.apartments || []).forEach((apt) => {
              Object.entries(apt.menu_summary || {}).forEach(([menu, count]) => {
                mergedMenuSummary[menu] = (mergedMenuSummary[menu] || 0) + Number(count || 0);
              });
            });
            const merged = toCoreMenuSummary(mergedMenuSummary);
            if (merged.total_count > 0) {
              setAllSummary(merged);
              setAllSummarySource('dashboard');
              setAllSummaryAdjustmentText('');
              return;
            }
          }
        }

        // 저장된 차트가 없으면 실시간(관리자페이지) 전체합계 조회
        const liveResp = await fetch(
          `${apiBase}/api/admin/chart-builder/live-summary/${deliveryDate}?apply_adjustments=${applyAdjustments ? 'true' : 'false'}`,
          {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          }
        );
        if (!liveResp.ok) {
          setAllSummary(null);
          setAllSummarySource(null);
          setAllSummaryAdjustmentText('');
          return;
        }
        const liveData: LiveSummaryResponse = await liveResp.json();
        const s = liveData.summary || {};
        const liveSummary: CoreMenuSummary = {
          total_count: Number(s.total_count || 0),
          korean_1_count: Number(s.korean_1_count || 0),
          korean_2_count: Number(s.korean_2_count || 0),
          simple_count: Number(s.simple_count || 0),
          salad_count: Number(s.salad_count || 0),
          sandwich_count: Number(s.sandwich_count || 0),
        };
        if (liveSummary.total_count > 0) {
          setAllSummary(liveSummary);
          setAllSummarySource('live');
          const adj = liveData.adjustment;
          if (adj?.applied) {
            setAllSummaryAdjustmentText(
              `취소분 반영: ${adj.cancellation_count || 0}건 조회, ${adj.removed_count || 0}건 차감`
            );
          } else if (adj?.error) {
            setAllSummaryAdjustmentText(`취소분 반영 실패: ${adj.error}`);
          } else {
            setAllSummaryAdjustmentText('');
          }
        } else {
          setAllSummary(null);
          setAllSummarySource(null);
          setAllSummaryAdjustmentText('');
        }
      } catch {
        setAllSummary(null);
        setAllSummarySource(null);
        setAllSummaryAdjustmentText('');
      } finally {
        setAllSummaryLoading(false);
      }
    };

    fetchAllSummary();
  }, [apiBase, deliveryDate, showAllSummary, token, applyAdjustments]);

  const handleParse = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!apartmentName.trim()) {
      setStatus('아파트를 선택하세요.', 'error');
      return;
    }
    if (!inputText.trim()) {
      setStatus('홈페이지 원본 데이터를 입력하세요.', 'error');
      return;
    }

    setParsing(true);
    setMessage('');
    try {
      const response = await fetch(`${apiBase}/api/admin/chart-builder/parse`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apartment_name: apartmentName,
          input_text: inputText,
          delivery_date: deliveryDate,
          include_line_sort: includeLineSort,
          apply_adjustments: applyAdjustments,
          treat_all_as_existing: treatAllAsExisting,
        }),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, '파싱 실패'));
      }

      const data = await response.json();
      setPayload(data.payload || null);
      setPreviewOrders(data.preview_orders || []);
      const adjustment = (data.adjustment || {}) as AdjustmentSummary;
      if (adjustment.applied) {
        setStatus(`파싱 완료: ${data.parsed_count || 0}건 (취소 ${adjustment.cancellation_count || 0}건 반영, ${adjustment.removed_count || 0}건 제외)`);
      } else if (adjustment.error) {
        setStatus(`파싱 완료: ${data.parsed_count || 0}건 (취소 반영 실패: ${adjustment.error})`, 'error');
      } else {
        setStatus(`파싱 완료: ${data.parsed_count || 0}건`);
      }
    } catch (err: any) {
      setStatus(normalizeFetchError(err, apiBase, '파싱 중 오류가 발생했습니다.'), 'error');
      setPayload(null);
      setPreviewOrders([]);
    } finally {
      setParsing(false);
    }
  };

  const handleAutoFetch = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!apartmentName.trim()) {
      setStatus('아파트를 선택하세요.', 'error');
      return;
    }

    setAutoFetching(true);
    setMessage('');
    try {
      const response = await fetch(`${apiBase}/api/admin/chart-builder/auto-fetch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apartment_name: apartmentName,
          delivery_date: deliveryDate,
          use_selenium: true,
          apply_adjustments: applyAdjustments,
          treat_all_as_existing: treatAllAsExisting,
        }),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, '자동조회 실패'));
      }

      const data = await response.json();
      const fetchedText = String(data.input_text || '');
      setInputText(fetchedText);
      setPayload(data.payload || null);
      setPreviewOrders(data.preview_orders || []);
      const adjustment = (data.adjustment || {}) as AdjustmentSummary;
      if (adjustment.applied) {
        setStatus(`자동조회 완료: ${data.parsed_count || 0}건 (취소 ${adjustment.cancellation_count || 0}건 반영, ${adjustment.removed_count || 0}건 제외)`);
      } else if (adjustment.error) {
        setStatus(`자동조회 완료: ${data.parsed_count || 0}건 (취소 반영 실패: ${adjustment.error})`, 'error');
      } else {
        setStatus(`자동조회 완료: ${data.parsed_count || 0}건`);
      }
    } catch (err: any) {
      setStatus(normalizeFetchError(err, apiBase, '자동조회 중 오류가 발생했습니다.'), 'error');
    } finally {
      setAutoFetching(false);
    }
  };

  const handleUpload = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    const uploadOne = async (chartPayload: ChartPayload) => {
      const response = await fetch(`${apiBase}/api/charts/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chartPayload),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `${chartPayload.apartment_name} 차트 등록 실패`));
      }

      const data = await response.json().catch(() => ({}));
      let postProcessText = '';
      if (token && chartPayload) {
        try {
          const tomsResp = await fetch(`${apiBase}/api/admin/chart-builder/post-upload-toms`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              apartment_name: chartPayload.apartment_name,
              delivery_date: chartPayload.delivery_date,
              orders_by_dong: chartPayload.orders_by_dong,
              is_personal_delivery: chartPayload.is_personal_delivery || false,
            }),
          });
          if (tomsResp.ok) {
            const tomsData = await tomsResp.json();
            if (tomsData?.scheduled) {
              postProcessText = `TOMS ${tomsData.mode === 'immediate' ? '즉시실행' : '예약'} ${tomsData.row_count || 0}건`;
            } else if (tomsData?.reason) {
              postProcessText = `TOMS 미실행: ${tomsData.reason}`;
            }
          } else {
            const errData = await tomsResp.json().catch(() => ({}));
            postProcessText = `TOMS 후처리 실패: ${errData.detail || tomsResp.status}`;
          }
        } catch (postErr: any) {
          postProcessText = `TOMS 후처리 실패: ${postErr?.message || 'error'}`;
        }
      }

      return {
        chartId: data.chart_id,
        postProcessText,
      };
    };

    if (!registerAllApartments && !payload) {
      setStatus('먼저 파싱을 실행하세요.', 'error');
      return;
    }

    if (registerAllApartments && apartments.length === 0) {
      setStatus('등록할 아파트가 없습니다.', 'error');
      return;
    }

    setUploading(true);
    setMessage('');
    try {
      if (!registerAllApartments) {
        const result = await uploadOne(payload as ChartPayload);
        setStatus(`차트 등록 완료 (${result.chartId ? `ID: ${result.chartId}` : '성공'})${result.postProcessText ? ` | ${result.postProcessText}` : ''}`);
      } else {
        let success = 0;
        let fail = 0;
        let noData = 0;
        const errors: string[] = [];

        for (let i = 0; i < apartments.length; i += 1) {
          const apt = apartments[i];
          const aptName = apt.apartment_name;
          setStatus(`[${i + 1}/${apartments.length}] ${aptName} 자동조회/등록 중...`);

          try {
            const fetchResp = await fetch(`${apiBase}/api/admin/chart-builder/auto-fetch`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                apartment_name: aptName,
                delivery_date: deliveryDate,
                use_selenium: true,
              apply_adjustments: applyAdjustments,
              treat_all_as_existing: treatAllAsExisting,
            }),
          });

            if (!fetchResp.ok) {
              const errText = await readErrorMessage(fetchResp, `${aptName} 자동조회 실패`);
              if (fetchResp.status === 404 || errText.includes('데이터가 없습니다')) {
                noData += 1;
                continue;
              }
              fail += 1;
              errors.push(errText);
              continue;
            }

            const fetched = await fetchResp.json();
            const chartPayload = fetched?.payload as ChartPayload | undefined;
            if (!chartPayload || !chartPayload.orders_by_dong) {
              fail += 1;
              errors.push(`${aptName} payload 생성 실패`);
              continue;
            }

            await uploadOne(chartPayload);
            success += 1;

            if (aptName === apartmentName) {
              setInputText(String(fetched.input_text || ''));
              setPayload(chartPayload);
              setPreviewOrders(fetched.preview_orders || []);
            }
          } catch (e: any) {
            fail += 1;
            errors.push(`${aptName}: ${e?.message || 'unknown error'}`);
          }
        }

        const baseMsg = `전체 아파트 차트 등록 완료: 성공 ${success}개, 데이터없음 ${noData}개, 실패 ${fail}개`;
        if (fail > 0 && errors.length > 0) {
          setStatus(`${baseMsg} | ${errors.slice(0, 2).join(' | ')}`, 'error');
        } else {
          setStatus(baseMsg);
        }
      }
    } catch (err: any) {
      setStatus(normalizeFetchError(err, apiBase, '차트 등록 중 오류가 발생했습니다.'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const sortedMenuSummary = useMemo(() => {
    if (!payload) return [];
    return Object.entries(payload.menu_summary || {}).sort((a, b) => b[1] - a[1]);
  }, [payload]);

  const fixedMenuSummary = useMemo(() => {
    if (!payload) return null;
    const core = payload.core_menu_summary;
    if (core) return core;
    return toCoreMenuSummary(payload.menu_summary || {});
  }, [payload]);

  const displayedAllSummary = useMemo(() => {
    if (!showAllSummary) return null;
    return allSummary;
  }, [showAllSummary, allSummary]);

  return (
    <div className="chart-builder-page">
      <header className="cb-header">
        <button className="cb-back-btn" onClick={() => navigate('/admin')}>
          ← 대시보드
        </button>
        <h1>홈페이지 입력 → 정렬 → 차트 등록</h1>
      </header>

      {message && (
        <div className={`cb-message ${messageType === 'error' ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <section className="cb-card">
        <div className="cb-grid">
          <label>아파트</label>
          <select value={apartmentName} onChange={(e) => setApartmentName(e.target.value)} className="cb-input">
            {apartments.map((apt) => (
              <option key={apt.apartment_name} value={apt.apartment_name}>
                {apt.apartment_name}
              </option>
            ))}
          </select>

          <label>배송일</label>
          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            className="cb-input"
          />

          <label>정렬 옵션</label>
          <label className="cb-checkbox">
            <input
              type="checkbox"
              checked={includeLineSort}
              onChange={(e) => setIncludeLineSort(e.target.checked)}
            />
            동별 라인설정 기준 정렬 사용
          </label>

          <label>취소분 반영</label>
          <label className="cb-checkbox">
            <input
              type="checkbox"
              checked={applyAdjustments}
              onChange={(e) => setApplyAdjustments(e.target.checked)}
            />
            조정데이터(취소분) 자동 적용
          </label>

          <label>합계 보기</label>
          <label className="cb-checkbox">
            <input
              type="checkbox"
              checked={showAllSummary}
              onChange={(e) => setShowAllSummary(e.target.checked)}
            />
            전체보기(기본): 해당 날짜 전체 아파트 합계 우선 표시
          </label>

          <label>등록 범위</label>
          <label className="cb-checkbox">
            <input
              type="checkbox"
              checked={registerAllApartments}
              onChange={(e) => setRegisterAllApartments(e.target.checked)}
            />
            전체 아파트 차트 등록(기본)
          </label>

          <label>기존고객 처리</label>
          <label className="cb-checkbox">
            <input
              type="checkbox"
              checked={treatAllAsExisting}
              onChange={(e) => setTreatAllAsExisting(e.target.checked)}
            />
            이번 등록분은 모두 기존고객으로 처리(보냉가방 자동 표시 안 함)
          </label>
        </div>

        <div className="cb-text-wrap">
          <label>홈페이지 원본 데이터</label>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="cb-textarea"
            placeholder={`예시:\n2025061909475239\t101\t702\t신현민\n한식 2인 (1 개)\n2025-07-24`}
          />
        </div>

        <div className="cb-actions">
          <button className="cb-fetch-btn" onClick={handleAutoFetch} disabled={autoFetching}>
            {autoFetching ? '자동조회 중...' : '자동조회(셀레니움)'}
          </button>
          <button className="cb-parse-btn" onClick={handleParse} disabled={parsing}>
            {parsing ? '파싱 중...' : '파싱/정렬 실행'}
          </button>
          <button
            className="cb-upload-btn"
            onClick={handleUpload}
            disabled={uploading || (!registerAllApartments && !payload)}
          >
            {uploading ? '등록 중...' : (registerAllApartments ? '전체 차트 등록' : '현재 아파트 차트 등록')}
          </button>
        </div>
      </section>

      {payload && (
        <section className="cb-card">
          <h2>파싱 결과</h2>
          <div className="cb-result-grid">
            <div>총 항목: <strong>{payload.total_count}</strong></div>
            <div>아파트: <strong>{payload.apartment_name}</strong></div>
            <div>배송일: <strong>{payload.delivery_date}</strong></div>
            <div>동 수: <strong>{Object.keys(payload.orders_by_dong || {}).length}</strong></div>
          </div>

          {showAllSummary && (
            <>
              <h3>전체 아파트 합계</h3>
              {allSummaryLoading ? (
                <div className="cb-summary-empty">전체 합계 불러오는 중...</div>
              ) : displayedAllSummary ? (
                <div className="cb-fixed-summary-grid">
                  <div className="cb-fixed-summary-item total">총 개수 <strong>{displayedAllSummary.total_count}</strong></div>
                  <div className="cb-fixed-summary-item">한식 1인 <strong>{displayedAllSummary.korean_1_count}</strong></div>
                  <div className="cb-fixed-summary-item">한식 2인 <strong>{displayedAllSummary.korean_2_count}</strong></div>
                  <div className="cb-fixed-summary-item">간편식 <strong>{displayedAllSummary.simple_count}</strong></div>
                  <div className="cb-fixed-summary-item">샐러드 <strong>{displayedAllSummary.salad_count}</strong></div>
                  <div className="cb-fixed-summary-item">샌드위치 <strong>{displayedAllSummary.sandwich_count}</strong></div>
                </div>
              ) : (
                <div className="cb-summary-empty">해당 날짜에 등록된 전체 차트가 없습니다.</div>
              )}

              <div className="cb-summary-note">
                {allSummarySource === 'live'
                  ? '해당 날짜 관리자 페이지 실시간 전체조회 기준 합계입니다.'
                  : '해당 날짜에 등록된 전체 아파트 차트 기준 합계입니다.'}
              </div>
              {allSummaryAdjustmentText && (
                <div className="cb-summary-note">{allSummaryAdjustmentText}</div>
              )}
            </>
          )}

          {!showAllSummary && fixedMenuSummary && (
            <>
              <h3>선택 아파트 메뉴 요약</h3>
              <div className="cb-fixed-summary-grid">
                <div className="cb-fixed-summary-item total">총 개수 <strong>{fixedMenuSummary.total_count}</strong></div>
                <div className="cb-fixed-summary-item">한식 1인 <strong>{fixedMenuSummary.korean_1_count}</strong></div>
                <div className="cb-fixed-summary-item">한식 2인 <strong>{fixedMenuSummary.korean_2_count}</strong></div>
                <div className="cb-fixed-summary-item">간편식 <strong>{fixedMenuSummary.simple_count}</strong></div>
                <div className="cb-fixed-summary-item">샐러드 <strong>{fixedMenuSummary.salad_count}</strong></div>
                <div className="cb-fixed-summary-item">샌드위치 <strong>{fixedMenuSummary.sandwich_count}</strong></div>
              </div>
            </>
          )}

          {!showAllSummary && (
            <>
              <h3>메뉴 합계</h3>
              <div className="cb-menu-list">
                {sortedMenuSummary.map(([menu, count]) => (
                  <span key={menu} className="cb-chip">{menu}: {count}</span>
                ))}
              </div>
            </>
          )}

          {showAllSummary && (
            <>
              <h3>메뉴 합계 (선택 아파트 상세)</h3>
              <div className="cb-menu-list">
                {sortedMenuSummary.map(([menu, count]) => (
                  <span key={menu} className="cb-chip">{menu}: {count}</span>
                ))}
              </div>
            </>
          )}

          <h3>미리보기 (최대 200건)</h3>
          <div className="cb-preview-table-wrap">
            <table className="cb-preview-table">
              <thead>
                <tr>
                  <th>동</th>
                  <th>호</th>
                  <th>고객명</th>
                  <th>메뉴</th>
                  <th>배송일</th>
                </tr>
              </thead>
              <tbody>
                {previewOrders.map((order, idx) => (
                  <tr key={`${order.dong}-${order.ho}-${idx}`}>
                    <td>{order.dong}</td>
                    <td>{order.ho}</td>
                    <td>{order.customer_name}</td>
                    <td>{order.menu}</td>
                    <td>{order.delivery_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default ChartBuilder;
